import { useEffect, useState } from 'react'
import { useParams, useNavigate, useSearchParams } from 'react-router-dom'
import { api, mediaUrl } from '../services/api.js'
import { usePageTitle } from '../hooks/usePageTitle.js'
import { useAuthStore } from '../stores/auth.js'
import { useTreeStore } from '../stores/tree.js'
import { formatDateDisplay } from '../components/shared/PartialDateInput.jsx'
import KnowledgeBox from '../components/person/KnowledgeBox.jsx'
import EditPersonPanel from '../components/person/EditPersonPanel.jsx'
import AddRelationshipModal from '../components/tree/AddRelationshipModal.jsx'
import RelationshipsPanel from '../components/person/RelationshipsPanel.jsx'
import SuggestChangeModal from '../components/person/SuggestChangeModal.jsx'
import SuggestionsPanel from '../components/person/SuggestionsPanel.jsx'
import DocumentsPanel from '../components/person/DocumentsPanel.jsx'
import LivedThroughPanel from '../components/person/LivedThroughPanel.jsx'
import TheirWorldLauncher from '../components/person/TheirWorldLauncher.jsx'
import ArchiveMatchesPanel from '../components/person/ArchiveMatchesPanel.jsx'
import OriginResearchPanel from '../components/person/OriginResearchPanel.jsx'
import RequestResearchModal from '../components/mysteries/RequestResearchModal.jsx'
import EnrichFromKGModal from '../components/person/EnrichFromKGModal.jsx'
import { purchasesAllowed } from '../utils/platform.js'
import Collapsible from '../components/shared/Collapsible.jsx'
import PhotoGalleryPanel from '../components/person/PhotoGalleryPanel.jsx'
import UpgradeInline from '../components/shared/UpgradeInline.jsx'

function getUserId(token) {
  try { return JSON.parse(atob(token.split('.')[1])).sub } catch { return null }
}

export default function PersonPage() {
  const { treeId, personId } = useParams()
  const [searchParams] = useSearchParams()
  // ?fromMystery=<id> — landed here from the Mysteries marketplace.
  // Solver gets Origin access to a person on a tree they don't own;
  // the agent's proposals will carry from_mystery_id for attribution
  // and the auto-applier's solver-safety rule blocks Tier 1 instant.
  const fromMysteryId = searchParams.get('fromMystery') || null
  const navigate = useNavigate()
  const { token } = useAuthStore()
  const userTier = useAuthStore(s => s.user?.tier || 'free')
  const setFocalPerson = useTreeStore(s => s.setFocalPerson)
  const [person, setPerson] = useState(null)
  const [tree, setTree] = useState(null)
  const [suggesting, setSuggesting] = useState(false)
  const [context, setContext] = useState(null)
  const [story, setStory] = useState(null)
  const [loading, setLoading] = useState(true)
  const [generating, setGenerating] = useState(null) // 'context' | 'story'
  const [editing, setEditing] = useState(false)
  const [connectingRelationship, setConnectingRelationship] = useState(false)
  const [treePersons, setTreePersons] = useState([])
  const [treeRelationships, setTreeRelationships] = useState([])
  const [aiError, setAiError] = useState(null)
  const [aiTierError, setAiTierError] = useState(null)
  const [requestingResearch, setRequestingResearch] = useState(false)
  const [enriching, setEnriching] = useState(false)

  useEffect(() => {
    Promise.all([
      api.get(`/persons/${personId}`),
      treeId ? api.get(`/trees/${treeId}`) : Promise.resolve(null)
    ]).then(([p, t]) => {
      setPerson(p)
      setTree(t)
      setFocalPerson(personId, { ...p, treeId, treeName: t?.name })
    }).catch(() => {
      // Person not found — clear stale focal person from store so dashboard doesn't link to it
      setFocalPerson(null, null)
    }).finally(() => setLoading(false))
  }, [personId, treeId])

  useEffect(() => {
    if (!treeId) return
    // Load each independently so a blip on one doesn't blank both —
    // the previous Promise.all meant a slow /relationships during an
    // active Wikidata harvest left the person page with an empty
    // partners/siblings view.
    api.get(`/persons?tree_id=${treeId}`).then(setTreePersons).catch(() => {})
    api.get(`/relationships?tree_id=${treeId}`).then(setTreeRelationships).catch(() => {})
  }, [treeId])

  async function generateContext() {
    setAiError(null); setAiTierError(null)
    setGenerating('context')
    try {
      const result = await api.post(`/ai/context/${personId}`)
      setContext(result.content || result)
    } catch (err) {
      if (err.code === 'TIER_LIMIT') setAiTierError(err)
      else setAiError('Could not generate historical context. Please try again.')
    } finally {
      setGenerating(null)
    }
  }

  async function generateStory() {
    setAiError(null); setAiTierError(null)
    setGenerating('story')
    try {
      const result = await api.post(`/ai/story/${personId}`)
      setStory(result.content || result)
    } catch (err) {
      if (err.code === 'TIER_LIMIT') setAiTierError(err)
      else setAiError('Could not generate life story. Please try again.')
    } finally {
      setGenerating(null)
    }
  }

  const displayFirst = person?.preferred_name || person?.first_name
  const fullName = [displayFirst, person?.last_name].filter(Boolean).join(' ') || 'Unknown'
  const formalName = person?.preferred_name
    ? [person.first_name, person.last_name].filter(Boolean).join(' ')
    : null
  usePageTitle(person ? fullName : '')

  if (loading) return <div className="loading">Loading</div>
  if (!person) return <div className="empty-state"><h3>Person not found</h3></div>

  return (
    <div>
      <div className="page-header page-header--person">
        <div className="page-header__top">
          <div style={{ display: 'flex', alignItems: 'flex-start', gap: '1.5rem' }}>
            {(person.photo_thumb_key || person.photo_thumb_url) && (
              <img
                src={person.photo_thumb_url || mediaUrl(person.photo_thumb_key)}
                alt={fullName}
                style={{
                  width: '96px', height: '96px', borderRadius: '50%',
                  objectFit: 'cover', border: '3px solid #fff',
                  boxShadow: '0 0 0 1px var(--gold, #c8a56e), 0 4px 12px rgba(0,0,0,0.06)',
                  flexShrink: 0, marginTop: '0.25rem'
                }}
              />
            )}
            <div>
              <div className="page-header__label">
                <span style={{ cursor: 'pointer' }} onClick={() => navigate(`/tree/${treeId}`)}>
                  ← Back to tree
                </span>
              </div>
              <h1 className="page-header__title">{fullName}</h1>
              {formalName && (
                <div style={{ fontSize: '0.85rem', color: 'var(--text-muted)', marginBottom: '0.25rem' }}>
                  {formalName}
                </div>
              )}
              <PersonMetaWithCitations person={person} />

              {/* Shared-person banner — only when the canonical person is
                  referenced by more than one tree. Tells researcher 2 that
                  their edits will be visible to others AND that recent
                  changes may have come from someone else. */}
              {person.shared_with_tree_count > 1 && (
                <div style={{
                  marginTop: '0.6rem', padding: '0.5rem 0.75rem',
                  background: 'color-mix(in srgb, var(--gold) 7%, white)',
                  border: '1px solid var(--gold)', borderRadius: 6,
                  fontSize: '0.78rem', color: 'var(--text)', lineHeight: 1.4,
                  maxWidth: '640px',
                }}>
                  <strong>Shared person</strong> — tracked across {person.shared_with_tree_count} trees.
                  Edits you make are visible to everyone who has them in their tree.
                  {person.last_edited_by_name && person.last_edited_at && (
                    <span style={{ display: 'block', marginTop: '0.2rem', color: 'var(--text-muted)' }}>
                      Last edited by {person.last_edited_by_name} · {new Date(person.last_edited_at).toLocaleDateString()}
                    </span>
                  )}
                </div>
              )}

            </div>
          </div>
          {(() => {
            // Header action routing:
            //   - Creator of the canonical person → Edit directly (always).
            //   - Tree owner, person only in this tree → Edit directly.
            //   - Shared historical person (in >1 tree) and NOT creator →
            //     Suggest change only. The API enforces the same gate via
            //     EDIT_REQUIRES_SUGGESTION.
            //   - Non-owner viewer → Suggest change.
            const userId = getUserId(token)
            const isOwner   = tree?.owner_id === userId
            const isCreator = person.created_by === userId
            const isShared  = person.shared_with_tree_count > 1 && person.is_historical
            const canEditDirectly = isCreator || (isOwner && !isShared)
            return (
              <div style={{ display: 'flex', gap: '0.5rem', flexShrink: 0 }}>
                {canEditDirectly ? (
                  <button onClick={() => setEditing(true)} className="btn btn-primary btn-sm">Edit</button>
                ) : (
                  <button onClick={() => setSuggesting(true)} className="btn btn-ghost btn-sm"
                    style={{ color: 'var(--text-muted)', borderColor: 'var(--border)' }}>
                    Suggest change
                  </button>
                )}
              </div>
            )
          })()}
        </div>
      </div>

      {(aiTierError || aiError) && (
        <div style={{ padding: '0 2.5rem', maxWidth: 1320 }}>
          {aiTierError && <div style={{ marginTop: '1rem' }}><UpgradeInline error={aiTierError} /></div>}
          {aiError && (
            <div className="form-error" style={{ marginTop: '1rem' }}>
              {aiError}
              <button
                onClick={() => setAiError(null)}
                className="btn btn-ghost btn-sm"
                style={{ marginLeft: '0.75rem', padding: '0.15rem 0.5rem' }}
              >Dismiss</button>
            </div>
          )}
        </div>
      )}

      <div className="person-page-with-rail">
      {/* LEFT COLUMN — Origin, Actions (tiles), then relational panels */}
      <div className="person-page">
        {person.is_historical && (tree?.owner_id === getUserId(token) || fromMysteryId) && (
          <OriginResearchPanel
            personId={personId}
            treeId={treeId}
            personName={[person.first_name, person.last_name].filter(Boolean).join(' ') || 'this person'}
            canResearch={true}
            fromMysteryId={fromMysteryId}
          />
        )}
        {/* Manual research request (Layer 3) — owner authors a mystery for
            this person without an Origin run. Owner-only; solvers (who arrive
            via ?fromMystery) don't see it. */}
        {person.is_historical && tree?.owner_id === getUserId(token) && (
          <div className="person-request-research">
            <button
              type="button"
              className="btn btn-sm person-request-research__btn"
              onClick={() => setEnriching(true)}
            >
              ✨ Enrich from Knowledge Graph
            </button>
            <button
              type="button"
              className="btn btn-sm person-request-research__btn"
              onClick={() => setRequestingResearch(true)}
            >
              🔎 Request research help
            </button>
            <span className="person-request-research__hint">
              Pull in what other researchers already found, or post the gap as a mystery.
            </span>
          </div>
        )}
        <ActionsPanel
          isOwner={tree?.owner_id === getUserId(token)}
          canConnect={treePersons.length > 1}
          onGenerateContext={generateContext}
          onGenerateStory={generateStory}
          onConnect={() => setConnectingRelationship(true)}
          onAddPhoto={() => {
            // Route to the gallery's append-upload, NOT the edit panel —
            // EditPersonPanel's single photo field replaces the primary,
            // which is why "add photo" used to overwrite instead of adding
            // another. Fall back to the edit panel only if the gallery input
            // isn't mounted yet (still loading / not owner).
            const input = document.getElementById('person-gallery-photo-input')
            if (input) {
              input.click()
              input.closest('.polaroid-rail')?.scrollIntoView({ behavior: 'smooth', block: 'nearest' })
            } else {
              setEditing(true)
            }
          }}
          generating={generating}
          hasPhoto={!!(person.photo_key || person.photo_thumb_key)}
          personId={personId}
          variant="tiles"
        />
        <Collapsible
          title="Family"
          icon="👪"
          color="green"
          storageKey={`person-${personId}-family`}
          defaultOpen={true}
        >
          <RelationshipsPanel
            person={person}
            treePersons={treePersons}
            relationships={treeRelationships}
            treeId={treeId}
            onNavigate={id => navigate(`/tree/${treeId}/person/${id}`)}
            onRelationshipRemoved={relId => setTreeRelationships(prev => prev.filter(r => r.id !== relId))}
            onRelationshipUpdated={updated => setTreeRelationships(prev => prev.map(r => r.id === updated.id ? { ...r, ...updated } : r))}
          />
        </Collapsible>

        {tree?.owner_id === getUserId(token) && (
          <Collapsible
            title="Suggestions inbox"
            icon="✉️"
            color="amber"
            storageKey={`person-${personId}-suggestions`}
            defaultOpen={false}
          >
            <SuggestionsPanel
              treeId={treeId}
              personId={personId}
              onApproved={() => api.get(`/persons/${personId}`).then(setPerson)}
            />
          </Collapsible>
        )}

        <Collapsible
          title="Archive matches"
          icon="🗂️"
          color="blue"
          storageKey={`person-${personId}-archive`}
          defaultOpen={true}
        >
          <ArchiveMatchesPanel personId={personId} />
        </Collapsible>

        <Collapsible
          title="Lived through"
          icon="🕰️"
          color="red"
          storageKey={`person-${personId}-lived`}
          defaultOpen={true}
        >
          <LivedThroughPanel personId={personId} />
        </Collapsible>

        <TheirWorldLauncher
          personId={personId}
          personName={[person.first_name, person.last_name].filter(Boolean).join(' ')}
        />
      </div>

      {/* RIGHT COLUMN — media + AI output */}
      <div className="person-rail">
        <PhotoGalleryPanel
          personId={personId}
          canEdit={tree?.owner_id === getUserId(token)}
          userTier={userTier}
        />

        <Collapsible
          title="Documents"
          icon="📄"
          color="grey"
          storageKey={`person-${personId}-docs`}
          defaultOpen={true}
        >
          <DocumentsPanel
            personId={personId}
            canEdit={tree?.owner_id === getUserId(token)}
          />
        </Collapsible>

        <KnowledgeBox personId={personId} firstName={person.first_name} />

        {!context && !story && (
          <div className="empty-state" style={{ background: 'var(--paper)', borderRadius: '10px', padding: '2rem 1.5rem', textAlign: 'center' }}>
            <div className="empty-state__icon">✦</div>
            <h3 style={{ fontSize: '1.05rem' }}>Bring {displayFirst || 'this person'} to life</h3>
            <p style={{ fontSize: '.85rem' }}>Use the action buttons above to generate historical context or a life-story narrative.</p>
          </div>
        )}

        {context && (
          <div className="ai-output">
            <div className="ai-output__header">
              <span className="ai-output__label">Historical context</span>
              <span className="badge badge-green">AI generated</span>
            </div>
            <div className="ai-output__body">{context}</div>
          </div>
        )}

        {story && (
          <div className="ai-output ai-output--story">
            <div className="ai-output__header">
              <span className="ai-output__label">Life story</span>
              <span className="badge badge-gold">Narrative</span>
            </div>
            <div className="ai-output__body">{story}</div>
          </div>
        )}

        {person.notes && (
          <div className="ai-output">
            <div className="ai-output__header"><span className="ai-output__label">Notes</span></div>
            <div className="ai-output__body">{person.notes}</div>
          </div>
        )}
      </div>
      </div>

      {suggesting && (
        <SuggestChangeModal
          person={person}
          treeId={treeId}
          onClose={() => setSuggesting(false)}
        />
      )}

      {connectingRelationship && (
        <AddRelationshipModal
          treeId={treeId}
          persons={treePersons}
          preselectedPersonId={personId}
          onSaved={() => {
            api.get(`/persons/${personId}`).then(setPerson).catch(() => {})
            api.get(`/persons?tree_id=${treeId}`).then(setTreePersons).catch(() => {})
            api.get(`/relationships?tree_id=${treeId}`).then(setTreeRelationships).catch(() => {})
          }}
          onClose={() => setConnectingRelationship(false)}
        />
      )}

      {editing && (
        <EditPersonPanel
          person={person}
          onClose={() => setEditing(false)}
          onSaved={(updated) => {
            if (updated === null) {
              navigate(`/tree/${treeId}`)
            } else {
              setPerson(updated)
            }
          }}
        />
      )}

      {requestingResearch && (
        <RequestResearchModal
          treeId={treeId}
          personId={personId}
          personName={[person.first_name, person.last_name].filter(Boolean).join(' ') || null}
          onClose={() => setRequestingResearch(false)}
        />
      )}

      {enriching && (
        <EnrichFromKGModal
          personId={personId}
          treeId={treeId}
          personName={[person.first_name, person.last_name].filter(Boolean).join(' ') || 'this person'}
          onClose={() => setEnriching(false)}
          onApplied={() => {
            // Refresh person + the tree's persons/relationships so imported
            // relatives and updated fields show immediately.
            api.get(`/persons/${personId}`).then(setPerson).catch(() => {})
            api.get(`/persons?tree_id=${treeId}`).then(setTreePersons).catch(() => {})
            api.get(`/relationships?tree_id=${treeId}`).then(setTreeRelationships).catch(() => {})
          }}
        />
      )}
    </div>
  )
}

// Meta line + citation footer. When a fact was approved from a document
// suggestion, the server returns person.field_sources[field] with the source
// doc — we render a superscript number inline and list the numbered sources
// below, academic-paper style. No citations → just the meta line.
const DOC_TYPE_LABELS = {
  birth_cert: 'Birth certificate',
  death_cert: 'Death certificate',
  marriage_cert: 'Marriage certificate',
  census: 'Census record',
  military: 'Military record',
  photo: 'Photograph',
  newspaper: 'Newspaper',
  letter: 'Letter',
  other: 'Document',
}

function PersonMetaWithCitations({ person }) {
  const sources = person.field_sources || {}
  const uniqueDocs = []
  const docIndex = new Map()
  for (const src of Object.values(sources)) {
    if (!docIndex.has(src.doc_id)) {
      docIndex.set(src.doc_id, uniqueDocs.length + 1)
      uniqueDocs.push(src)
    }
  }

  const cite = (field) => {
    const src = sources[field]
    if (!src) return null
    return (
      <sup
        className="person-cite"
        title={`${DOC_TYPE_LABELS[src.doc_type] || 'Document'}${src.doc_date ? ` (${src.doc_date})` : ''}`}
      >
        [{docIndex.get(src.doc_id)}]
      </sup>
    )
  }

  const parts = []
  if (person.birth_date)  parts.push(<span key="bd">b. {formatDateDisplay(person.birth_date)}{cite('birth_date')}</span>)
  if (person.death_date)  parts.push(<span key="dd">d. {formatDateDisplay(person.death_date)}{cite('death_date')}</span>)
  if (person.cause_of_death) parts.push(<span key="cod">cause: {person.cause_of_death}{cite('cause_of_death')}</span>)
  if (person.birth_place) parts.push(<span key="bp">{person.birth_place}{cite('birth_place')}</span>)
  if (person.occupation)  parts.push(<span key="oc">{person.occupation}{cite('occupation')}</span>)

  return (
    <>
      <div className="page-header__meta">
        {parts.map((p, i) => (
          <span key={i}>{i > 0 && ' · '}{p}</span>
        ))}
      </div>
      {uniqueDocs.length > 0 && (
        <div className="person-sources">
          {uniqueDocs.map((s, i) => (
            <span key={s.doc_id} className="person-sources__item">
              [{i + 1}] {DOC_TYPE_LABELS[s.doc_type] || 'Document'}
              {s.source_label ? `, ${s.source_label}` : ''}
              {s.doc_date ? ` · ${s.doc_date}` : ''}
            </span>
          ))}
        </div>
      )}
    </>
  )
}

// Right-rail actions card. Holds the secondary actions that used to sit in
// the page header: generate historical context, life story, connect
// persons, add photo. Subtle styling so it reads as a companion to the
// photo rail below it, not a competing focal point.
function ActionsPanel({
  isOwner, canConnect, hasPhoto,
  onGenerateContext, onGenerateStory, onConnect, onAddPhoto,
  generating, personId,
  variant,  // 'tiles' for 2-col grid layout
}) {
  const [storyReport, setStoryReport] = useState(null)
  // Credit status lets Family users see "free this month" instead of the
  // list price. For Free/Plus `remaining` is always 0, so the default
  // €9.99 copy shows.
  const [credits, setCredits] = useState({ remaining: 0, tier: 'free' })
  const [orderingReport, setOrderingReport] = useState(false)

  useEffect(() => {
    api.get('/billing/catalogue')
      .then(c => setStoryReport(c?.storyReport || null))
      .catch(() => setStoryReport(null))
    api.get('/billing/story-credits')
      .then(setCredits)
      .catch(() => {})
  }, [])

  async function orderStoryReport() {
    if (!storyReport?.priceId) return
    setOrderingReport(true)
    try {
      // The backend auto-redeems a Family credit when available — we
      // hit the same endpoint either way; response `url` points to
      // Stripe Checkout OR /billing?credit=used. No branching needed here.
      const { url } = await api.post('/billing/checkout', {
        priceId: storyReport.priceId, mode: 'payment', personId,
      })
      window.location.href = url
    } catch (err) {
      alert(err.message || 'Failed to start checkout')
      setOrderingReport(false)
    }
  }

  const hasCredit = credits.remaining > 0
  const priceBlurb = storyReport ? `€${(storyReport.amount / 100).toFixed(2)}` : ''
  const reportLabel = orderingReport
    ? 'Preparing…'
    : hasCredit
      ? 'Order included Story Report · free this month'
      : `Order Story Report · ${priceBlurb}`
  const reportSubLabel = hasCredit
    ? 'One Story Report is included with your Family plan each month'
    : 'Designed narrative PDF for this ancestor'

  return (
    <div className={`person-actions ${variant === 'tiles' ? 'person-actions--tiles' : ''}`}>
      <div className="person-actions__label">Actions</div>
      <div className="person-actions__list">
        <button
          className="person-actions__item"
          onClick={onGenerateContext}
          disabled={!!generating}
        >
          <span className="person-actions__icon" aria-hidden>✦</span>
          <span className="person-actions__text">
            <span className="person-actions__name">
              {generating === 'context' ? 'Generating…' : 'Historical context'}
            </span>
            <span className="person-actions__sub">What was happening during their life</span>
          </span>
        </button>
        <button
          className="person-actions__item"
          onClick={onGenerateStory}
          disabled={!!generating}
        >
          <span className="person-actions__icon" aria-hidden>📖</span>
          <span className="person-actions__text">
            <span className="person-actions__name">
              {generating === 'story' ? 'Generating…' : 'Life story'}
            </span>
            <span className="person-actions__sub">A full grounded narrative</span>
          </span>
        </button>
        {canConnect && (
          <button className="person-actions__item" onClick={onConnect}>
            <span className="person-actions__icon" aria-hidden>↔</span>
            <span className="person-actions__text">
              <span className="person-actions__name">Connect persons</span>
              <span className="person-actions__sub">Parent, partner, sibling</span>
            </span>
          </button>
        )}
        {isOwner && !hasPhoto && (
          <button className="person-actions__item" onClick={onAddPhoto}>
            <span className="person-actions__icon" aria-hidden>📷</span>
            <span className="person-actions__text">
              <span className="person-actions__name">Add portrait photo</span>
              <span className="person-actions__sub">Upload via Edit</span>
            </span>
          </button>
        )}
        {/* Story Report: a credited (included) report is free, so it stays on
            native; the PAID purchase is hidden on native (Apple 3.1.1). */}
        {storyReport?.priceId && (hasCredit || purchasesAllowed()) && (
          <button
            className="person-actions__item"
            onClick={orderStoryReport}
            disabled={orderingReport}
          >
            <span className="person-actions__icon" aria-hidden>{hasCredit ? '✨' : '📄'}</span>
            <span className="person-actions__text">
              <span className="person-actions__name">{reportLabel}</span>
              <span className="person-actions__sub">{reportSubLabel}</span>
            </span>
          </button>
        )}
      </div>
    </div>
  )
}
